// ─────────────────────────────────────────────────────────────────────
// Account Security → Bulk go-live setup send (Round 13, decisions D9/D11)
//
// Preview → type SEND → background job on the API → progress polling →
// per-recipient results (with retry of failures). Nothing is sent by
// merely opening this panel; the API also refuses without the SEND token.
// ─────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Progress, Table, Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../api";

export default function BulkSetupSendPanel({ selectedEmpIds, onFinished }) {
    const [open, setOpen] = useState(false);
    const [includeReady, setIncludeReady] = useState(false);
    const [preview, setPreview] = useState(null);
    const [confirm, setConfirm] = useState("");
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);

    const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    useEffect(() => () => stopPolling(), []);

    const openPreview = async () => {
        setBusy(true); setPreview(null); setJob(null); setConfirm("");
        try {
            const res = await api.post("/admin/account-security/bulk/preview", { empIds: selectedEmpIds || [], includeReady });
            setPreview(res.data);
            setOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Could not build the preview.");
        } finally { setBusy(false); }
    };

    const start = async () => {
        setBusy(true);
        try {
            const res = await api.post("/admin/account-security/bulk/send-setup", { empIds: selectedEmpIds || [], includeReady, confirm });
            const jobId = res.data?.jobId;
            toast.info(`Bulk send started for ${res.data?.total} recipient(s).`);
            const poll = async () => {
                try {
                    const st = await api.get(`/admin/account-security/bulk/status/${jobId}`);
                    setJob(st.data);
                    if (st.data?.done) { stopPolling(); if (onFinished) onFinished(); }
                } catch { /* keep polling */ }
            };
            await poll();
            pollRef.current = setInterval(poll, 2000);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Could not start the bulk send.");
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        if (!job?.jobId) return;
        try { await api.post(`/admin/account-security/bulk/cancel/${job.jobId}`); toast.info("Cancellation requested."); }
        catch (e) { toast.error(e?.response?.data?.error || "Could not cancel."); }
    };

    const c = preview?.counts || {};
    const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;

    return (
        <>
            <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button color="danger" size="sm" onClick={openPreview} disabled={busy}>
                    <i className="ri-send-plane-line me-1" />
                    Bulk send setup links… {selectedEmpIds && selectedEmpIds.length > 0 ? `(${selectedEmpIds.length} selected)` : "(all who need setup)"}
                </Button>
                <label className="small text-muted mb-0">
                    <input type="checkbox" className="me-1" checked={includeReady} onChange={(e) => setIncludeReady(e.target.checked)} />
                    include accounts that are already Ready (forces them to choose a new password)
                </label>
            </div>

            <Modal isOpen={open} toggle={() => !busy && !job && setOpen(false)} size="lg">
                <ModalHeader toggle={() => !busy && (!job || job.done) && setOpen(false)}>Bulk setup-link send</ModalHeader>
                <ModalBody>
                    {!job && preview && (
                        <>
                            <p className="mb-2">
                                <strong>{preview.eligibleCount}</strong> employee(s) will receive a password setup e-mail (link valid 72 hours, single use).
                            </p>
                            <ul className="small text-muted mb-3">
                                <li>Skipped — not Active: {c.skippedInactive || 0}</li>
                                <li>Skipped — no e-mail on file: {c.skippedMissingEmail || 0}</li>
                                <li>Skipped — duplicate e-mail (data conflict): {c.skippedDataConflict || 0}</li>
                                <li>Skipped — already Ready (untick to include): {c.skippedReady || 0}</li>
                                {c.skippedItAdmin ? <li>Skipped — IT Admin accounts (only an IT Admin may send to them): {c.skippedItAdmin}</li> : null}
                                {c.hasOpenLink ? <li>Already have an open link (it will be replaced): {c.hasOpenLink}</li> : null}
                                {c.truncatedTo ? <li className="text-warning">Capped at {c.truncatedTo} recipients per run — run again for the rest.</li> : null}
                            </ul>
                            {!preview.frontendBaseUrlConfigured && <div className="alert alert-danger py-2">Frontend:BaseUrl is not configured on the API — links cannot be built. Fix the configuration first.</div>}
                            {preview.running && <div className="alert alert-warning py-2">A bulk send is already running.</div>}
                            <p className="small mb-1">Type <code>SEND</code> to confirm:</p>
                            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="SEND" style={{ maxWidth: 160 }} />
                        </>
                    )}
                    {job && (
                        <>
                            <Progress value={pct} className="mb-2" color={job.done ? (job.failed ? "warning" : "success") : "primary"}>{pct}%</Progress>
                            <div className="small mb-3">
                                {job.processed} / {job.total} processed ·{" "}
                                <Badge color="success">{job.sent} sent</Badge>{" "}
                                <Badge color="secondary">{job.skipped} skipped</Badge>{" "}
                                <Badge color="danger">{job.failed} failed</Badge>
                                {!job.done && <span className="ms-2"><Spinner size="sm" /> running…</span>}
                                {job.done && <span className="ms-2">{job.cancelRequested ? "cancelled" : "finished"}</span>}
                            </div>
                            {job.done && (job.failed > 0 || job.skipped > 0) && (
                                <Table size="sm" responsive className="mb-0" style={{ fontSize: 12 }}>
                                    <thead><tr><th>Emp ID</th><th>Result</th><th>Detail</th></tr></thead>
                                    <tbody>
                                        {(job.results || []).filter((r) => r.status !== "sent").map((r) => (
                                            <tr key={r.empId}><td><code>{r.empId}</code></td><td><Badge color={r.status === "failed" ? "danger" : "secondary"}>{r.status}</Badge></td><td>{r.message}</td></tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    {!job && (
                        <>
                            <Button color="light" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                            <Button color="danger" onClick={start}
                                disabled={busy || confirm !== "SEND" || !preview || preview.eligibleCount === 0 || preview.running || !preview.frontendBaseUrlConfigured}>
                                {busy ? <><Spinner size="sm" /> Starting…</> : `Send to ${preview?.eligibleCount ?? 0} employee(s)`}
                            </Button>
                        </>
                    )}
                    {job && !job.done && <Button color="warning" onClick={cancel}>Stop after the current recipient</Button>}
                    {job && job.done && <Button color="primary" onClick={() => { setOpen(false); setJob(null); }}>Close</Button>}
                </ModalFooter>
            </Modal>
        </>
    );
}
